import './StreamListHeader.css';

export interface StreamFilters {
  search: string;
  dataset: 'all' | 'following' | 'r3dlabs_teams';
  sort: {
    field: 'duration' | 'viewers';
    direction: 'asc' | 'desc';
  };
}

export class StreamListHeader {
  private container: HTMLElement;
  private filters: StreamFilters = {
    search: '',
    dataset: 'all',
    sort: {
      field: 'viewers',
      direction: 'desc'
    }
  };
  private isAdvancedExpanded = false;
  private isRefreshing = false;

  private onFiltersChange: (filters: StreamFilters) => void;
  private onRefresh: () => Promise<void>;

  constructor(
    container: HTMLElement, 
    onFiltersChange: (filters: StreamFilters) => void,
    onRefresh: () => Promise<void>
  ) {
    this.container = container;
    this.onFiltersChange = onFiltersChange;
    this.onRefresh = onRefresh;
    this.render();
    this.attachEventListeners();
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="stream-list-header">
        <div class="filter-row">
          <div class="search-container">
            <input 
              type="text" 
              class="search-input" 
              placeholder="Search streams..."
              value="${this.filters.search}"
            >
          </div>
          <button class="header-button refresh-button" title="Refresh streams">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M13.65 2.35A7.958 7.958 0 0 0 8 0a8 8 0 1 0 8 8h-2a6 6 0 1 1-1.35-3.65l-1.83 1.83 4.11.59-.59-4.11-1.69 1.69z"/>
            </svg>
          </button>
          <button class="header-button ${this.isAdvancedExpanded ? 'active' : ''}" title="Toggle advanced filters">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z"/>
            </svg>
          </button>
        </div>
        <div class="advanced-filters">
          <div>
            <div class="filter-group">
            <div class="filter-label">Show</div>
            <div class="filter-options">
              <button class="filter-option ${this.filters.dataset === 'all' ? 'active' : ''}" data-dataset="all">
                All
              </button>
              <button class="filter-option ${this.filters.dataset === 'following' ? 'active' : ''}" data-dataset="following">
                Following
              </button>
              <button class="filter-option ${this.filters.dataset === 'r3dlabs_teams' ? 'active' : ''}" data-dataset="r3dlabs_teams">
                R3dLabs Teams
              </button>
            </div>
          </div>
          <div class="filter-group">
            <div class="filter-label">Sort by</div>
            <div class="filter-options">
              <button 
                class="filter-option sort-option ${this.filters.sort.field === 'viewers' ? 'active' : ''} ${this.filters.sort.field === 'viewers' && this.filters.sort.direction === 'desc' ? 'desc' : ''}" 
                data-sort="viewers"
              >
                Viewers
              </button>
              <button 
                class="filter-option sort-option ${this.filters.sort.field === 'duration' ? 'active' : ''} ${this.filters.sort.field === 'duration' && this.filters.sort.direction === 'desc' ? 'desc' : ''}" 
                data-sort="duration"
              >
                Duration
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private attachEventListeners(): void {
    // Search input
    const searchInput = this.container.querySelector('.search-input') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.filters.search = (e.target as HTMLInputElement).value;
      this.onFiltersChange(this.filters);
    });

    // Refresh button
    const refreshButton = this.container.querySelector('.refresh-button');
    refreshButton?.addEventListener('click', async () => {
      if (this.isRefreshing) return;
      
      this.isRefreshing = true;
      refreshButton.classList.add('refreshing');
      
      try {
        await this.onRefresh();
      } finally {
        this.isRefreshing = false;
        refreshButton.classList.remove('refreshing');
      }
    });

    // Advanced filters toggle
    const advancedToggle = refreshButton?.nextElementSibling;
    const advancedFilters = this.container.querySelector('.advanced-filters');
    
    advancedToggle?.addEventListener('click', () => {
      this.isAdvancedExpanded = !this.isAdvancedExpanded;
      if (this.isAdvancedExpanded) {
        advancedFilters?.classList.add('expanded');
        advancedToggle.classList.add('active');
      } else {
        advancedFilters?.classList.remove('expanded');
        advancedToggle.classList.remove('active');
      }
    });

    // Dataset filters
    const datasetButtons = this.container.querySelectorAll('[data-dataset]');
    datasetButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const dataset = (e.currentTarget as HTMLElement).dataset.dataset as StreamFilters['dataset'];
        this.filters.dataset = dataset;
        
        datasetButtons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');
        
        this.onFiltersChange(this.filters);
      });
    });

    // Sort filters
    const sortButtons = this.container.querySelectorAll('[data-sort]');
    sortButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const field = (e.currentTarget as HTMLElement).dataset.sort as StreamFilters['sort']['field'];
        
        if (this.filters.sort.field === field) {
          // Toggle direction if same field
          this.filters.sort.direction = this.filters.sort.direction === 'asc' ? 'desc' : 'asc';
          button.classList.toggle('desc', this.filters.sort.direction === 'desc');
        } else {
          // Set new field with default desc direction
          this.filters.sort.field = field;
          this.filters.sort.direction = 'desc';
          
          sortButtons.forEach(btn => {
            btn.classList.remove('active', 'desc');
          });
          button.classList.add('active', 'desc');
        }
        
        this.onFiltersChange(this.filters);
      });
    });
  }

  public getFilters(): StreamFilters {
    return { ...this.filters };
  }
}
